import React from 'react'
import { ClientSentiment, CompetitorsPosition, SentimentScale } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../tokens'
import { SectionCard } from './SectionCard'
import { useClientSentimentSave } from './useClientSentimentSave'

interface Props {
  palette: SurfacePalette
  packetID: number
  sentiment: ClientSentiment | null | undefined
}

const scaleOptions: { value: Exclude<SentimentScale, null>; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No' },
]

const competitorsOptions: { value: Exclude<CompetitorsPosition, null>; label: string }[] = [
  { value: 'below', label: 'Below' },
  { value: 'equal', label: 'Equal' },
  { value: 'above', label: 'Above' },
]

function Segmented<T extends string>(props: {
  palette: SurfacePalette
  value: T | null
  options: { value: T; label: string }[]
  onChange: (v: T | null) => void
  ariaLabel: string
}) {
  const { palette, value, options, onChange, ariaLabel } = props
  return (
    <div role="radiogroup" aria-label={ariaLabel} style={{ display: 'inline-flex', gap: 4 }}>
      {options.map(opt => {
        const selected = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(selected ? null : opt.value)}
            style={{
              padding: '6px 12px',
              borderRadius: radius.sm,
              border: `1px solid ${selected ? palette.accent : palette.hairline}`,
              background: selected ? palette.accent : palette.surface,
              color: selected ? palette.accentInk : palette.text,
              fontFamily: type.family.sans,
              fontSize: type.size.body,
              fontWeight: selected ? type.weight.semibold : type.weight.regular,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

function FieldLabel({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: type.family.mono,
        fontSize: type.size.eyebrow,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: palette.textMuted,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  )
}

function NotesInput(props: {
  palette: SurfacePalette
  value: string | null
  onCommit: (v: string | null) => void
  placeholder: string
}) {
  const { palette, value, onCommit, placeholder } = props
  const [local, setLocal] = React.useState(value ?? '')
  React.useEffect(() => {
    setLocal(value ?? '')
  }, [value])
  return (
    <textarea
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        const next = local.trim() === '' ? null : local
        if (next !== (value ?? null)) onCommit(next)
      }}
      placeholder={placeholder}
      rows={2}
      style={{
        width: '100%',
        marginTop: 8,
        padding: '8px 10px',
        borderRadius: radius.sm,
        border: `1px solid ${palette.hairline}`,
        background: palette.surface,
        color: palette.text,
        fontFamily: type.family.sans,
        fontSize: type.size.body,
        resize: 'vertical',
      }}
    />
  )
}

export const ClientSentimentSection = ({ palette, packetID, sentiment }: Props) => {
  const { draft, saveField, indicator } = useClientSentimentSave(packetID, sentiment)

  return (
    <SectionCard
      palette={palette}
      sectionKey="client-sentiment"
      title="Client Sentiment"
      commentAnchor={{
        type: 'section',
        id: `${packetID}:client-sentiment`,
        label: 'Client Sentiment',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <FieldLabel palette={palette}>What did you like today?</FieldLabel>
          <NotesInput
            palette={palette}
            value={draft.likedToday}
            onCommit={v => saveField('likedToday', v)}
            placeholder="What stood out for the client this meeting…"
          />
        </div>

        <div>
          <FieldLabel palette={palette}>Are we below / equal / above competitors?</FieldLabel>
          <Segmented
            palette={palette}
            value={draft.competitorsPosition}
            options={competitorsOptions}
            onChange={v => saveField('competitorsPosition', v)}
            ariaLabel="Competitors position"
          />
          <NotesInput
            palette={palette}
            value={draft.competitorsNotes}
            onCommit={v => saveField('competitorsNotes', v)}
            placeholder="Notes (optional)"
          />
        </div>

        <div>
          <FieldLabel palette={palette}>Would you renew if your contract were up today?</FieldLabel>
          <Segmented
            palette={palette}
            value={draft.wouldRenew}
            options={scaleOptions}
            onChange={v => saveField('wouldRenew', v)}
            ariaLabel="Would renew today"
          />
          <NotesInput
            palette={palette}
            value={draft.wouldRenewNotes}
            onCommit={v => saveField('wouldRenewNotes', v)}
            placeholder="Notes (optional)"
          />
        </div>

        <div>
          <FieldLabel palette={palette}>Would you recommend us?</FieldLabel>
          <Segmented
            palette={palette}
            value={draft.wouldRecommend}
            options={scaleOptions}
            onChange={v => saveField('wouldRecommend', v)}
            ariaLabel="Would recommend"
          />
          <NotesInput
            palette={palette}
            value={draft.wouldRecommendNotes}
            onCommit={v => saveField('wouldRecommendNotes', v)}
            placeholder="Notes (optional)"
          />
        </div>

        <div
          style={{
            fontFamily: type.family.mono,
            fontSize: 11,
            color: palette.textFaint,
            minHeight: 14,
          }}
        >
          {indicator}
        </div>
      </div>
    </SectionCard>
  )
}
